import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Cloud, RefreshCw, MapPin, CheckCircle2, XCircle, AlertCircle, Plug, ArrowRight } from "lucide-react";
import { shortDate } from "@/lib/format";

export default function Settings() {
  const [providers, setProviders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState("");
  const [providerChoice, setProviderChoice] = useState("mock");
  const [syncResult, setSyncResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState([]);
  const [erp, setErp] = useState([]);
  const [erpDialog, setErpDialog] = useState(null);

  useEffect(() => {
    api.get("/tracking/providers").then(r => setProviders(r.data));
    api.get("/shipments").then(r => {
      setShipments(r.data);
      if (r.data.length > 0) setSelected(r.data[0].id);
    });
    api.get("/billing/subscriptions").then(r => setSubs(r.data));
    api.get("/integrations/erp").then(r => setErp(r.data));
  }, []);

  const sync = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/tracking/sync/${selected}`, null, { params: { provider: providerChoice } });
      setSyncResult(r.data);
      toast.success(`Synced from ${r.data.provider}${r.data.mocked ? ' (mocked)' : ''}`);
    } catch {
      toast.error("Sync failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Integrations &amp; Account</h1>
        <p className="text-sm text-zinc-500 mt-1">Connect carrier tracking providers and manage your subscription.</p>
      </div>

      {/* Tracking providers */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <Cloud className="w-4 h-4 text-[#002FA7]" />
          <h3 className="font-heading text-lg font-bold tracking-tight">Carrier Tracking Providers</h3>
        </div>
        <div className="p-5 grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {providers.map(p => (
            <div key={p.key} data-testid={`provider-${p.key}`} className={`bg-white p-5 ${p.active ? 'border-l-[3px] border-l-[#002FA7]' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-bold">{p.name}</div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em]">
                  {p.configured ? <CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A]" /> : <XCircle className="w-3.5 h-3.5 text-zinc-400" />}
                  {p.configured ? <span className="text-[#166534]">configured</span> : <span className="text-zinc-500">no key</span>}
                </div>
              </div>
              <div className="text-xs text-zinc-600 mb-3">
                {p.key === 'project44' && "Real-time multi-modal visibility platform. Set P44_API_KEY to enable."}
                {p.key === 'maersk_spot' && "Maersk container tracking + bookings. Set MAERSK_API_KEY to enable."}
                {p.key === 'mock' && "Deterministic demo data for development &amp; testing."}
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="inline-flex items-center gap-1 text-[#B45309]">
                  <AlertCircle className="w-3 h-3" /> MOCKED
                </span>
                {p.active && <span className="text-[10px] uppercase tracking-[0.12em] bg-[#E6EBF6] text-[#002FA7] px-2 py-0.5">Active</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-zinc-100 bg-[#FEF3C7] border-[#FCD34D]">
          <div className="flex items-start gap-2 text-xs text-[#B45309]">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold uppercase tracking-[0.12em]">MOCKED — </span>
              All providers currently return demo data. To enable live tracking, set <code className="bg-white px-1 mx-0.5 metric text-[10px] border border-[#FCD34D]">P44_API_KEY</code> or <code className="bg-white px-1 mx-0.5 metric text-[10px] border border-[#FCD34D]">MAERSK_API_KEY</code> in <code className="bg-white px-1 mx-0.5 metric text-[10px] border border-[#FCD34D]">backend/.env</code> then restart the backend.
            </div>
          </div>
        </div>
      </div>

      {/* Sync test */}
      <div className="bg-white border border-zinc-200 p-5">
        <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Test a Sync</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="data-label">Shipment</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="sync-shipment" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {shipments.slice(0, 20).map(s => <SelectItem key={s.id} value={s.id}>{s.id} · {s.bl_number}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="data-label">Provider</Label>
            <Select value={providerChoice} onValueChange={setProviderChoice}>
              <SelectTrigger data-testid="sync-provider" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {providers.map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="sync-btn" onClick={sync} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10">
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} />Sync now
          </Button>
        </div>
        {syncResult && (
          <div className="mt-5 border-t border-zinc-200 pt-5 grid lg:grid-cols-2 gap-5" data-testid="sync-result">
            <div>
              <div className="data-label mb-2">Position &amp; ETA</div>
              <div className="metric text-sm space-y-1">
                <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#002FA7]" />Lat {syncResult.vessel_position.lat}, Lon {syncResult.vessel_position.lon}</div>
                <div>Predicted ETA: <span className="font-semibold">{shortDate(syncResult.predicted_eta)}</span></div>
                <div>Confidence: <span className="font-semibold">{Math.round(syncResult.confidence * 100)}%</span></div>
              </div>
            </div>
            <div>
              <div className="data-label mb-2">Recent Events</div>
              <div className="space-y-1">
                {syncResult.events.slice(-4).reverse().map((ev, i) => (
                  <div key={i} className="text-xs flex items-baseline gap-2">
                    <span className="metric text-[10px] text-zinc-400 w-24">{shortDate(ev.timestamp)}</span>
                    <span className="font-medium">{ev.event_code}</span>
                    <span className="text-zinc-600">{ev.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ERP & Accounting integrations */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <Plug className="w-4 h-4 text-[#002FA7]" />
          <h3 className="font-heading text-lg font-bold tracking-tight">ERP &amp; Accounting Integrations</h3>
          <span className="ml-auto text-[10px] uppercase tracking-[0.12em] bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 border border-[#FDE68A]">Beta</span>
        </div>
        <div className="grid lg:grid-cols-3 gap-px bg-zinc-200 border-y border-zinc-200">
          {erp.map(p => (
            <div key={p.id} data-testid={`erp-${p.id}`} className={`bg-white p-5 ${p.connected ? "border-l-[3px] border-l-[#16A34A]" : ""}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-heading font-bold">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 mt-0.5">{p.category}</div>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em]">
                  {p.connected
                    ? <><CheckCircle2 className="w-3.5 h-3.5 text-[#16A34A]" /><span className="text-[#166534]">connected</span></>
                    : p.status === "requested"
                      ? <><AlertCircle className="w-3.5 h-3.5 text-[#F59E0B]" /><span className="text-[#B45309]">requested</span></>
                      : <><XCircle className="w-3.5 h-3.5 text-zinc-400" /><span className="text-zinc-500">not connected</span></>}
                </div>
              </div>
              <p className="text-xs text-zinc-600 mb-4 min-h-[2.5rem]">{p.description}</p>
              <Button
                data-testid={`erp-cta-${p.id}`}
                size="sm"
                variant={p.connected ? "ghost" : "outline"}
                className="rounded-none h-8 text-xs"
                disabled={p.connected}
                onClick={() => setErpDialog(p)}
              >
                {p.connected ? "Configured" : p.status === "requested" ? "Request sent" : <>Request setup <ArrowRight className="w-3 h-3 ml-1.5" /></>}
              </Button>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 text-xs text-zinc-500 border-t border-zinc-100">
          Need a system that isn't listed? <a href="mailto:integrations@wedelvr.com" className="text-[#002FA7] hover:underline">Tell us</a> and we'll add it to the roadmap.
        </div>
      </div>

      <ErpRequestDialog
        provider={erpDialog}
        onClose={() => setErpDialog(null)}
        onSent={() => {
          setErpDialog(null);
          api.get("/integrations/erp").then(r => setErp(r.data));
        }}
      />

      {/* Subscription */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h3 className="font-heading text-lg font-bold tracking-tight">Subscription</h3>
        </div>
        <div className="p-5">
          {subs.length === 0 ? (
            <div className="text-sm text-zinc-500">No active subscription. <a href="/#pricing" className="text-[#002FA7] hover:underline">Choose a plan →</a></div>
          ) : (
            <table className="w-full text-sm" data-testid="subs-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">{["Plan", "Status", "Amount", "Started", "Session"].map(h => <th key={h} className="px-4 py-2 data-label">{h}</th>)}</tr>
              </thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id} className="border-b border-zinc-100">
                    <td className="px-4 py-2.5 font-medium">{s.plan_label || s.plan_id}</td>
                    <td className="px-4 py-2.5"><span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]">{s.status}</span></td>
                    <td className="px-4 py-2.5 metric">{s.amount ? `$${s.amount} ${(s.currency || '').toUpperCase()}` : '—'}</td>
                    <td className="px-4 py-2.5 metric text-xs">{shortDate(s.started_at)}</td>
                    <td className="px-4 py-2.5 metric text-[10px] text-zinc-500">{(s.session_id || '').slice(0, 24)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ErpRequestDialog({ provider, onClose, onSent }) {
  const [form, setForm] = useState({ company: "", contact_email: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (provider) setForm({ company: "", contact_email: "", notes: "" }); }, [provider]);

  const submit = async () => {
    if (!provider) return;
    setBusy(true);
    try {
      await api.post(`/integrations/erp/${provider.id}/request`, form);
      toast.success(`${provider.name} setup requested. We'll be in touch within 24h.`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!provider} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Request {provider?.name} setup</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Our team will reach out to coordinate API keys and a sandbox connection. Typical setup time: 2–5 business days.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="data-label">Company name</Label><Input data-testid="erp-form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none mt-1.5 h-10" /></div>
          <div><Label className="data-label">Contact email</Label><Input data-testid="erp-form-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none mt-1.5 h-10 metric" /></div>
          <div><Label className="data-label">Notes (optional)</Label><Input data-testid="erp-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. 'NetSuite tenant in US-East, ~500 POs/month'" className="rounded-none mt-1.5 h-10" /></div>
          {provider?.env_keys?.length > 0 && (
            <div className="bg-zinc-50 border border-zinc-200 p-3 text-[11px] text-zinc-600">
              <div className="data-label mb-1">Keys we'll need from you</div>
              <div className="metric">{provider.env_keys.join(" · ")}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button data-testid="erp-form-submit" onClick={submit} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">{busy ? "Sending..." : "Send request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
